import { Bot, Clock, DollarSign, FileText, Heart, MessageSquare, Search, Target, User, BookOpen } from 'lucide-react'

type TabType = 'overview' | 'summary' | 'tasks' | 'findings' | 'questions' | 'members' | 'agents' | 'messages' | 'timeline' | 'documents' | 'active-agents' | 'budget' | 'creator' | 'donors'

interface ProjectTabsProps {
  activeTab: TabType
  onTabChange: (tab: TabType) => void
  projectType: 'mission' | 'research'
  tasksCount: number
  findingsCount: number
  membersCount: number
  agentsCount: number
  donorsCount?: number
  questionsCount?: number
}

export default function ProjectTabs({
  activeTab,
  onTabChange,
  projectType,
  tasksCount,
  findingsCount,
  agentsCount,
  donorsCount = 0,
  questionsCount = 0,
}: ProjectTabsProps) {
  const tabs: { id: TabType; label: string; icon: React.ReactNode; count?: number; show?: boolean }[] = [
    { id: 'overview',      label: 'Overview',   icon: <Search size={14} />,       show: true },
    { id: 'summary',       label: 'Summary',    icon: <BookOpen size={14} />,     show: projectType === 'research' },
    { id: 'findings',      label: 'Findings',   icon: <FileText size={14} />,     count: findingsCount, show: projectType === 'research' },
    { id: 'questions',     label: 'Questions',  icon: <MessageSquare size={14} />, count: questionsCount, show: projectType === 'research' },
    { id: 'tasks',         label: 'Tasks',      icon: <Target size={14} />,       count: tasksCount, show: projectType === 'mission' },
    { id: 'timeline',      label: 'Timeline',   icon: <Clock size={14} />,        show: true },
    { id: 'messages',      label: 'Messages',   icon: <MessageSquare size={14} />, show: true },
    { id: 'agents',        label: 'AI Agents',  icon: <Bot size={14} />,          count: agentsCount, show: true },
    { id: 'donors',        label: 'Donors',     icon: <Heart size={14} />,        count: donorsCount, show: projectType === 'research' },
    { id: 'budget',        label: 'Budget',     icon: <DollarSign size={14} />,   show: projectType === 'research' },
    { id: 'creator',       label: 'Creator',    icon: <User size={14} />,         show: true },
  ]

  return (
    <div style={{
      display: 'flex',
      gap: '4px',
      padding: '8px',
      background: 'var(--card-bg)',
      border: '1px solid var(--border-color)',
      borderRadius: '12px',
      flexWrap: 'wrap',
      marginBottom: '20px',
    }}>
      {tabs.filter(t => t.show !== false).map(tab => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 14px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: activeTab === tab.id ? '600' : '500',
            color: activeTab === tab.id ? '#fff' : 'var(--text-secondary)',
            background: activeTab === tab.id
              ? 'linear-gradient(135deg, #667eea, #764ba2)'
              : 'transparent',
            transition: 'all 0.2s',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={e => {
            if (activeTab !== tab.id) {
              e.currentTarget.style.background = 'var(--hover-bg, #f8fafc)'
              e.currentTarget.style.color = 'var(--text-primary)'
            }
          }}
          onMouseLeave={e => {
            if (activeTab !== tab.id) {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.color = 'var(--text-secondary)'
            }
          }}
        >
          {tab.icon}
          {tab.label}
          {tab.count !== undefined && tab.count > 0 && (
            <span style={{
              background: activeTab === tab.id ? 'rgba(255,255,255,0.25)' : 'var(--border-color)',
              color: activeTab === tab.id ? '#fff' : 'var(--text-secondary)',
              borderRadius: '10px',
              padding: '1px 7px',
              fontSize: '11px',
              fontWeight: '600',
            }}>
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  )
}
